import React, { useState } from "react";
import useBackdropClose from "../../hooks/useBackdropClose";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { API_URL } from "../../config/constants";
import {
  Overlay, Modal, SectionTitle, Grid, Field, Label, Input, Textarea, Hint,
  ActionRow, CancelButton, SaveButton,
} from "./styles";

const NAME_MAX = 150;
const BIO_MAX = 500;

/**
 * Solo se editan nombre público y bio. Usuario y email son credenciales, y el
 * hospital determina qué pacientes ve el terapeuta: los tres se muestran
 * deshabilitados para dar contexto, no para tocarlos.
 */
const EditAccountModal = ({ account, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(account.display_name || "");
  const [bio, setBio] = useState(account.bio || "");
  const [loading, setLoading] = useState(false);

  const nameMissing = !displayName.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (nameMissing || loading) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/me/account`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ display_name: displayName.trim(), bio: bio.trim() }),
      });
      if (!res.ok) throw new Error();

      // El nombre público se muestra en la videollamada desde localStorage.
      const stored = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem(
        "user",
        JSON.stringify({ ...stored, display_name: displayName.trim() })
      );

      toast.success(t("account.toastSaved"));
      onSaved();
    } catch {
      toast.error(t("account.toastError"));
      setLoading(false);
    }
  };

  return (
    <Overlay {...useBackdropClose(onClose)}>
      <Modal onClick={(e) => e.stopPropagation()} onSubmit={handleSubmit}>
        <SectionTitle>{t("auth.publicInformation")}</SectionTitle>
        <Grid>
          <Field>
            <Label htmlFor="ea-name">{t("auth.displayName")}</Label>
            <Input
              id="ea-name"
              maxLength={NAME_MAX}
              $error={nameMissing}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <Hint $error={nameMissing}>{t("auth.displayNameHint")}</Hint>
          </Field>

          <Field>
            <Label htmlFor="ea-bio">{t("auth.bio")}</Label>
            <Textarea
              id="ea-bio"
              maxLength={BIO_MAX}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
            />
            <Hint>
              {t("auth.bioHint")} {bio.length}/{BIO_MAX}
            </Hint>
          </Field>
        </Grid>

        <SectionTitle>{t("auth.therapistAccount")}</SectionTitle>
        <Grid>
          <Field>
            <Label htmlFor="ea-hospital">{t("auth.hospitalCode")}</Label>
            <Input id="ea-hospital" value={account.hospital_code || ""} disabled readOnly />
            <Hint>{t("account.readOnlyHospital")}</Hint>
          </Field>

          <Field>
            <Label htmlFor="ea-username">{t("auth.username")}</Label>
            <Input id="ea-username" value={account.username || ""} disabled readOnly />
            <Hint>{t("account.readOnlyField")}</Hint>
          </Field>

          <Field>
            <Label htmlFor="ea-email">{t("auth.emailAddress")}</Label>
            <Input id="ea-email" value={account.email || ""} disabled readOnly />
            <Hint>{t("account.readOnlyField")}</Hint>
          </Field>
        </Grid>

        <ActionRow>
          <CancelButton type="button" onClick={onClose}>{t("account.cancel")}</CancelButton>
          <SaveButton type="submit" disabled={nameMissing || loading}>
            {loading ? t("account.saving") : t("account.save")}
          </SaveButton>
        </ActionRow>
      </Modal>
    </Overlay>
  );
};

export default EditAccountModal;
